import React, { useState, useMemo, useEffect } from 'react';
import { Task, Category, AnalyticsMetrics, MetricKey } from '../types';
import { analyticsService } from '../services/analyticsService';
import { geminiService } from '../services/geminiService';
import { MetricHistoryModal } from './MetricHistoryModal';
import {
  Target, Zap, Activity, Sparkles, PieChart,
  Loader2, Layers, Clock, TrendingUp, TrendingDown,
  Minus, ArrowUpRight, ArrowDownRight, CheckCircle2
} from 'lucide-react';

interface Props {
  tasks: Task[];
  categories: Category[];
}

export const AnalyticsDashboard: React.FC<Props> = ({ tasks, categories }) => {
  const metrics = useMemo(() => analyticsService.calculateMetrics(tasks), [tasks]);
  const quadDist = useMemo(() => analyticsService.getDistributionByQuadrant(tasks), [tasks]);
  const catDist = useMemo(() => analyticsService.getDistributionByCategory(tasks, categories), [tasks, categories]);

  const [dailyReflection, setDailyReflection] = useState<string>("Analyzing your workflow...");
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeMetric, setActiveMetric] = useState<MetricKey | null>(null);

  // Coach Insight: once per calendar day, summarizing all activity
  const todayKey = new Date().toISOString().split('T')[0]; // e.g. "2026-03-17"

  useEffect(() => {
    const fetchReflection = async () => {
      const cachedDate = localStorage.getItem('vanguard_reflection_date');

      // One insight per calendar day — serve cache if already generated today
      if (cachedDate === todayKey) {
        setDailyReflection(localStorage.getItem('vanguard_current_reflection') || "Metrics stable.");
        return;
      }
      setIsSyncing(true);
      try {
        const res = await geminiService.generateVentureAudit(metrics, tasks);
        setDailyReflection(res);
        localStorage.setItem('vanguard_current_reflection', res);
        localStorage.setItem('vanguard_reflection_date', todayKey);
      } catch (e) {
        console.error("[Vanguard] Coach Insight fetch failed:", e);
        setDailyReflection("Insight temporarily unavailable. Your strategic focus remains strong.");
      } finally { setIsSyncing(false); }
    };
    fetchReflection();
  }, [todayKey]);

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in pb-20">

      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Performance</h2>
        <p className="text-slate-500 text-sm">Insights into your personal effectiveness.</p>
      </div>

      {/* AI Insight */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
          {isSyncing ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
        </div>
        <div className="space-y-1">
          <h5 className="text-xs font-bold text-indigo-600 uppercase tracking-wide">Coach Insight</h5>
          <p className="text-slate-700 leading-relaxed text-sm">"{dailyReflection}"</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* KPI Cards — Clickable for Historical Graphs */}
        {(() => {
          const scoreStatus = metrics.prioriScore >= 100 ? 'strong' : metrics.prioriScore >= 30 ? 'building' : 'starting';
          return (
            <div
              onClick={() => setActiveMetric('prioriScore')}
              className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm cursor-pointer hover:border-blue-200 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Target size={18}/></div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  scoreStatus === 'strong' ? 'bg-emerald-50 text-emerald-600' : scoreStatus === 'building' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'
                }`}>{scoreStatus === 'strong' ? 'Strong' : scoreStatus === 'building' ? 'Building' : 'Get Started'}</span>
              </div>
              <div className="space-y-1 mb-3">
                <h4 className="text-3xl font-bold text-slate-900">{metrics.prioriScore}<span className="text-sm font-medium text-slate-400 ml-1">pts</span></h4>
                <p className="text-xs font-semibold text-slate-700">Clarity Score</p>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">Weighted impact points. Q1 tasks score highest, Q4 lowest.</p>
              <p className="text-[10px] text-blue-500 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View 30-day trend</p>
            </div>
          );
        })()}

        {(() => {
          const velStatus = metrics.questVelocity >= 70 ? 'strong' : metrics.questVelocity >= 40 ? 'moderate' : 'low';
          return (
            <div
              onClick={() => setActiveMetric('questVelocity')}
              className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm cursor-pointer hover:border-emerald-200 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><TrendingUp size={18}/></div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  velStatus === 'strong' ? 'bg-emerald-50 text-emerald-600' : velStatus === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'
                }`}>{velStatus === 'strong' ? 'On Track' : velStatus === 'moderate' ? 'Moderate' : 'Needs Focus'}</span>
              </div>
              <div className="space-y-1 mb-3">
                <h4 className="text-3xl font-bold text-slate-900">{metrics.questVelocity}<span className="text-sm font-medium text-slate-400 ml-0.5">%</span></h4>
                <p className="text-xs font-semibold text-slate-700">Completion Rate</p>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">Tasks completed vs. added in the last 7 days.</p>
              <p className="text-[10px] text-emerald-500 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View 30-day trend</p>
            </div>
          );
        })()}

        {(() => {
          const dwStatus = metrics.deepWorkRatio >= 40 ? 'strong' : metrics.deepWorkRatio >= 20 ? 'moderate' : 'low';
          return (
            <div
              onClick={() => setActiveMetric('deepWorkRatio')}
              className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm cursor-pointer hover:border-amber-200 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Activity size={18}/></div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  dwStatus === 'strong' ? 'bg-emerald-50 text-emerald-600' : dwStatus === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'
                }`}>{dwStatus === 'strong' ? 'Strategic' : dwStatus === 'moderate' ? 'Balanced' : 'Reactive'}</span>
              </div>
              <div className="space-y-1 mb-3">
                <h4 className="text-3xl font-bold text-slate-900">{metrics.deepWorkRatio}<span className="text-sm font-medium text-slate-400 ml-0.5">%</span></h4>
                <p className="text-xs font-semibold text-slate-700">Strategic Focus (Q2)</p>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">Percentage of tasks in your Strategic Moat quadrant.</p>
              <p className="text-[10px] text-amber-500 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View 30-day trend</p>
            </div>
          );
        })()}
      </div>

      {/* Secondary Effectiveness Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Momentum</p>
          <p className="text-xl font-bold text-slate-900">{metrics.momentumConsistency}<span className="text-xs text-slate-400 ml-0.5">%</span></p>
          <p className="text-[10px] text-slate-400 mt-1">Active days this week</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Avg. Completion</p>
          <p className="text-xl font-bold text-slate-900">{metrics.timeToCompletion}<span className="text-xs text-slate-400 ml-0.5">hrs</span></p>
          <p className="text-[10px] text-slate-400 mt-1">Creation to done</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">On-Time</p>
          <p className="text-xl font-bold text-slate-900">{metrics.planningAccuracy}<span className="text-xs text-slate-400 ml-0.5">%</span></p>
          <p className="text-[10px] text-slate-400 mt-1">Completed before due</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Focus Leverage</p>
          <p className="text-xl font-bold text-slate-900">{metrics.focusLeverage}<span className="text-xs text-slate-400 ml-0.5">x</span></p>
          <p className="text-[10px] text-slate-400 mt-1">Q2 vs Q3/Q4 ratio</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Matrix Distribution */}
        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
           <div className="flex items-center gap-3 mb-6">
              <PieChart size={20} className="text-slate-400" />
              <h3 className="font-bold text-slate-900">Matrix Balance</h3>
           </div>
           <div className="space-y-5">
              {[
                { label: 'Do First (Q1)', key: 'Q1', color: 'bg-rose-500' },
                { label: 'Schedule (Q2)', key: 'Q2', color: 'bg-blue-500' },
                { label: 'Delegate (Q3)', key: 'Q3', color: 'bg-amber-500' },
                { label: 'Postpone (Q4)', key: 'Q4', color: 'bg-slate-400' }
              ].map((item: any) => (
                <div key={item.key} className="space-y-2">
                   <div className="flex justify-between text-xs font-medium text-slate-600">
                      <span>{item.label}</span>
                      <span>{quadDist[item.key as keyof typeof quadDist] || 0}</span>
                   </div>
                   <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${((quadDist[item.key as keyof typeof quadDist] || 0) / Math.max(1, tasks.length)) * 100}%` }}
                      ></div>
                   </div>
                </div>
              ))}
           </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
           <div className="flex items-center gap-3 mb-6">
              <Layers size={20} className="text-slate-400" />
              <h3 className="font-bold text-slate-900">Categories</h3>
           </div>
           <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
              {catDist.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">No data available</div>
              ) : catDist.map((cat, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                   <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }}></div>
                      <span className="text-sm font-medium text-slate-700">{cat.name}</span>
                   </div>
                   <span className="text-xs font-bold text-slate-400">{cat.count}</span>
                </div>
              ))}
           </div>
        </div>
      </div>

      {/* Historical Graph Modal */}
      {activeMetric && (
        <MetricHistoryModal
          metricKey={activeMetric}
          tasks={tasks}
          onClose={() => setActiveMetric(null)}
        />
      )}
    </div>
  );
};
